<template>
    <PageWrapper>
        <template #actions>
            <BackButton @click="router.go(-1)" />
            <ActionsMenu v-if="userStore.can('can_withdraw_money')" />
        </template>
        <template #modals>
            <CreateWithdrawalModal
                v-if="wallet?.id"
                v-model:open="isWithdrawalModalOpen"
                :wallets="wallet ? [wallet] : []"
                @cancel="isWithdrawalModalOpen = false"
                @submit="handleWithdrawalSubmit"
            />
        </template>
        <template #headline>
            <fa icon="fa-wallet" />
            {{ $t('wallet') }} {{ wallet?.id }}
        </template>
        <SummaryWrapper>
            <WalletSummary v-if="wallet && wallet?.id" />
            <template #sidebar>
                <AccountSummary />
                <PaymentAccountSummary
                    v-if="paymentAccount && paymentAccount?.id"
                />
            </template>
        </SummaryWrapper>
        <AccordionBasic
            v-if="wallet && wallet?.id"
            class="mb-2"
            title="wallet_transactions"
            @update:open="transactionsAccordionOpen = $event"
        >
            <TransactionsTable
                v-if="transactionsAccordionOpen"
                :initial-filters="transactionsInitialFilters"
            />
        </AccordionBasic>
        <AccordionBasic
            v-if="wallet && wallet?.id"
            title="wallet_money_withdrawals"
            class="mb-2"
            :open="false"
        >
            <WithdrawalTable />
        </AccordionBasic>
    </PageWrapper>
</template>

<script setup lang="ts">
import AccordionBasic from '@src/components/AccordionBasic.vue'
import SummaryWrapper from '@src/components/SummaryWrapper.vue'
import { useWithdrawalTable } from '@src/composables/accounts/useWithdrawalTable'
import { useActionsMenu, useEnum, useSummary } from '@src/composables/common'
import { useAccountSummary } from '@src/composables/summaries/useAccountSummary'
import { usePaymentAccountSummary } from '@src/composables/summaries/usePaymentAccountSummary'
import { WalletStatus } from '@src/enums'
import CreateWithdrawalModal from '@src/partials/accounts/CreateWithdrawalModal.vue'
import BackButton from '@src/partials/actions/BackButton.vue'
import PageWrapper from '@src/partials/pages/PageWrapper.vue'
import TransactionsTable from '@src/partials/transactions/TransactionsTable.vue'
import {
    CreateMoneyWithdrawal,
    LegalProfile,
    MoneyWithdrawal,
    NaturalProfile,
} from '@src/types'
import { PaymentAccount } from '@src/types/PaymentAccount'
import { SummaryItem } from '@src/types/Summary'
import {
    createMoneyWithdrawal as apiCreateMoneyWithdrawal,
    getLegalProfile,
    getNaturalProfile,
    syncWallet,
} from '@src/utils/api/accounts'
import { getWallet, WalletData } from '@src/utils/api/wallets'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import moment from 'moment'
import { computed, onMounted, reactive, Ref, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const i18n = useI18n()

const wallet = ref<WalletData | null>(null)
const paymentAccount = ref<PaymentAccount | null>(null)
// Use initialized profile instances to avoid null access inside summaries
const naturalProfile = ref<NaturalProfile>(new NaturalProfile())
const legalProfile = ref<LegalProfile>(new LegalProfile())

const transactionsAccordionOpen = ref(false)
const transactionsInitialFilters = reactive<{
    natural_profile_id?: string | null
    legal_profile_id?: string | null
}>({})

const moneyWithdrawals = ref<MoneyWithdrawal[]>([])
const moneyWithdrawalsNum = ref(0)

const toast = useToastStore()

const { getEnumLabel } = useEnum()

const userStore = useUserStore()
const isWithdrawalModalOpen = ref<boolean>(false)

const walletDetailsItems = computed<SummaryItem[]>((): SummaryItem[] => {
    return [
        {
            label: i18n.t('id'),
            value: wallet.value?.id,
            copy: true,
        },
        {
            label: i18n.t('type'),
            value:
                String(wallet.value?.type)[0].toUpperCase() +
                String(wallet.value?.type).slice(1),
        },
        {
            label: i18n.t('email'),
            value: wallet.value?.email,
        },
        {
            label: i18n.t('full_name'),
            value: wallet.value?.full_name,
        },
        {
            label: i18n.t('external_id'),
            value: wallet.value?.external_id,
        },
        {
            label: i18n.t('external_internal_id'),
            value: wallet.value?.external_internal_id,
        },
        {
            label: i18n.t('status'),
            value: wallet.value?.status
                ? getEnumLabel(WalletStatus, wallet.value?.status)
                : '-',
        },
        {
            label: i18n.t('balance'),
            value: wallet.value?.balance,
        },
        {
            label: i18n.t('created_at'),
            value: moment(wallet.value?.created_at).format('DD.MM.yyyy HH:mm'),
        },
        {
            label: i18n.t('updated_at'),
            value: moment(wallet.value?.updated_at).format('DD.MM.yyyy HH:mm'),
        },
    ]
})

const { Summary: WalletSummary } = useSummary(walletDetailsItems, {
    title: 'wallet',
})

const { PaymentAccountSummary } = usePaymentAccountSummary(
    computed(() => paymentAccount.value ?? null)
)

const { AccountSummary } = useAccountSummary(
    naturalProfile as Ref<NaturalProfile>,
    legalProfile as Ref<LegalProfile>
)

const { WithdrawalTable } = useWithdrawalTable(
    moneyWithdrawals,
    moneyWithdrawalsNum
)

const { ActionsMenu } = useActionsMenu(
    {
        id: 'wallet-actions-menu',
        align: 'right',
    },
    [
        {
            label: 'withdraw_money',
            icon: 'fa-credit-card',
            color: 'text-gray-500',
            action: () => (isWithdrawalModalOpen.value = true),
            condition: () => userStore.can('can_withdraw_money'),
        },
    ]
)

const handleWithdrawalSubmit = async (data: CreateMoneyWithdrawal) => {
    const body = new CreateMoneyWithdrawal()
    body.amount = data.amount
    body.bank_account_id = data.bank_account_id
    body.comment = data.comment
    body.natural_profile_id = wallet.value?.account_id ?? null
    const response = await apiCreateMoneyWithdrawal(body)
    if (response.status === 200 && response.data.success) {
        toast.success(i18n.t('toast_success_money_withdrawal_created'))
        isWithdrawalModalOpen.value = false
        if (wallet.value?.type === 'natural') {
            await syncWallet(naturalProfile.value.account_uid as string)
        } else {
            await syncWallet(legalProfile.value.account_uid as string)
        }
    }
}

onMounted(async () => {
    const response = await getWallet(route.params.id as string)
    wallet.value = response.data
    paymentAccount.value = response.data.payment_account
    moneyWithdrawals.value = Array.isArray(response.data.money_withdrawals)
        ? response.data.money_withdrawals
        : []
    moneyWithdrawalsNum.value = moneyWithdrawals.value.length
    if (wallet.value?.type === 'natural') {
        const naturalProfileRequest = await getNaturalProfile(
            response.data.profile_id as string
        )
        naturalProfile.value = naturalProfileRequest.data.natural_profile
        await syncWallet(naturalProfile.value.account_uid as string)
    } else {
        const legalProfileRequest = await getLegalProfile(
            response.data.profile_id as string
        )
        legalProfile.value = legalProfileRequest.data.legal_profile
        const naturalProfileRequest = await getNaturalProfile(
            legalProfileRequest.data.legal_profile.natural_profile_uid as string
        )
        await syncWallet(legalProfile.value.account_uid as string)
        naturalProfile.value = naturalProfileRequest.data.natural_profile
    }

    if (wallet.value?.type === 'natural') {
        const profile = naturalProfile.value as NaturalProfile
        transactionsInitialFilters.natural_profile_id =
            profile.natural_profile_uid
    } else if (wallet.value?.type === 'legal') {
        const profile = legalProfile.value as LegalProfile
        transactionsInitialFilters.legal_profile_id = profile.legal_profile_uid
    }
})
</script>
